import { Dialog } from '@base-ui/react/dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { BadgeCheck, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAmpli } from '@/ampli';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import useDirectoryAgentClaimRequest from '../hooks/use-directory-agent-claim-request';

const claimRequestSchema = z.object({
  email: z.email('Enter a valid email address.'),
});

type ClaimRequestValues = z.infer<typeof claimRequestSchema>;

export type DirectoryAgentClaimOption = {
  id: string;
  name: string;
  slug: string;
  postcode?: string | null;
};

type DirectoryAgentClaimRequestDialogSingleProps = {
  agentId: string;
  agentName: string;
  agentSlug: string;
  agentOptions?: undefined;
};

type DirectoryAgentClaimRequestDialogPickerProps = {
  agentOptions: DirectoryAgentClaimOption[];
  agentId?: undefined;
  agentName?: undefined;
  agentSlug?: undefined;
};

type DirectoryAgentClaimRequestDialogProps = (
  | DirectoryAgentClaimRequestDialogSingleProps
  | DirectoryAgentClaimRequestDialogPickerProps
) & {
  triggerLabel?: string;
  triggerVariant?: 'button' | 'link';
};

const buttonTriggerClassName =
  'inline-flex h-11 items-center justify-center gap-2 rounded-full border border-line-strong bg-white px-5 text-sm font-medium text-ink transition-[background-color,transform] duration-150 ease-out hover:bg-chip active:scale-[0.98] motion-reduce:transition-none';

const linkTriggerClassName =
  'font-medium text-accent-strong underline decoration-accent-strong/40 underline-offset-2 transition-[color,text-decoration-color] duration-150 ease-out hover:text-accent hover:decoration-accent motion-reduce:transition-none';

const DirectoryAgentClaimRequestDialog = ({
  triggerLabel = 'Claim this profile',
  triggerVariant = 'button',
  ...props
}: DirectoryAgentClaimRequestDialogProps) => {
  const ampli = useAmpli();
  const claimRequest = useDirectoryAgentClaimRequest();
  const isPickerMode = props.agentOptions != null;
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const sortedAgentOptions = useMemo(() => {
    if (!isPickerMode) {
      return [];
    }

    return [...props.agentOptions].sort((left, right) =>
      left.name.localeCompare(right.name, 'en-GB'),
    );
  }, [isPickerMode, props]);

  const selectedAgent = isPickerMode
    ? sortedAgentOptions.find((option) => option.id === selectedAgentId)
    : null;

  const resolvedAgentName = isPickerMode
    ? (selectedAgent?.name ?? 'your agency')
    : props.agentName;

  const form = useForm<ClaimRequestValues>({
    resolver: zodResolver(claimRequestSchema),
    defaultValues: { email: '' },
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen || isPickerMode) {
      return;
    }

    ampli((client) =>
      client.directoryAgentClaimOpened({ agent_slug: props.agentSlug }),
    );
  };

  const handleAgentChange = (agentId: string) => {
    setSelectedAgentId(agentId);
    const agent = sortedAgentOptions.find((option) => option.id === agentId);
    if (agent == null) {
      return;
    }

    ampli((client) =>
      client.directoryAgentClaimOpened({ agent_slug: agent.slug }),
    );
  };

  const handleSubmit = async ({ email }: ClaimRequestValues) => {
    if (isPickerMode) {
      if (selectedAgent == null) {
        form.setError('root', { message: 'Choose your branch.' });
        return;
      }

      const error = await claimRequest.requestClaim({
        agentId: selectedAgent.id,
        agentSlug: selectedAgent.slug,
        email,
      });
      if (!error) {
        return;
      }

      if (error === 'VERIFICATION_FAILED') {
        form.setError('root', {
          message: 'Please complete the check and try again.',
        });
        return;
      }

      if (error === 'AGENT_NOT_FOUND') {
        form.setError('root', {
          message: 'This profile is no longer available to claim.',
        });
        return;
      }

      form.setError('root', {
        message: 'Something went wrong. Please try again.',
      });
      return;
    }

    const error = await claimRequest.requestClaim({
      agentId: props.agentId,
      agentSlug: props.agentSlug,
      email,
    });
    if (!error) {
      return;
    }

    if (error === 'VERIFICATION_FAILED') {
      form.setError('root', {
        message: 'Please complete the check and try again.',
      });
      return;
    }

    if (error === 'AGENT_NOT_FOUND') {
      form.setError('root', {
        message: 'This profile is no longer available to claim.',
      });
      return;
    }

    form.setError('root', {
      message: 'Something went wrong. Please try again.',
    });
  };

  const triggerClassName =
    triggerVariant === 'link' ? linkTriggerClassName : buttonTriggerClassName;

  return (
    <Dialog.Root onOpenChange={handleOpenChange}>
      <Dialog.Trigger className={triggerClassName}>
        {triggerVariant === 'button' ? (
          <BadgeCheck aria-hidden="true" className="size-4" />
        ) : null}
        {triggerLabel}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-ink/35 backdrop-blur-sm transition-opacity duration-200 ease-out data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 motion-reduce:transition-none" />
        <Dialog.Viewport className="fixed inset-0 z-50 flex items-center justify-center p-5">
          <Dialog.Popup className="surface-card relative w-full max-w-md p-6 transition-[transform,opacity] duration-200 ease-out data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 motion-reduce:transition-none">
            <Dialog.Close
              aria-label="Close"
              className="absolute top-4 right-4 flex size-10 items-center justify-center rounded-full text-ink-muted hover:bg-chip hover:text-ink"
            >
              <X aria-hidden="true" className="size-5" />
            </Dialog.Close>
            {claimRequest.isSubmitted ? (
              <div className="flex flex-col gap-3 pr-8" role="status">
                <Dialog.Title className="m-0 text-2xl font-medium tracking-[-0.025em]">
                  Request received
                </Dialog.Title>
                <Dialog.Description className="m-0 leading-relaxed text-ink-soft">
                  We’ll be in touch about claiming {resolvedAgentName}’s
                  profile.
                </Dialog.Description>
              </div>
            ) : (
              <Form {...form}>
                <form
                  className="flex flex-col gap-5"
                  noValidate
                  onSubmit={form.handleSubmit(handleSubmit)}
                >
                  {claimRequest.verificationWidget}
                  <div className="flex flex-col gap-2 pr-8">
                    <Dialog.Title className="m-0 text-2xl font-medium tracking-[-0.025em]">
                      {isPickerMode
                        ? 'Claim your profile'
                        : `Claim ${props.agentName}`}
                    </Dialog.Title>
                    <Dialog.Description className="m-0 leading-relaxed text-ink-soft">
                      Leave your work email and we’ll get in touch to verify
                      you, so you can correct details and confirm your fees.
                      This does not create an account.
                    </Dialog.Description>
                  </div>
                  {isPickerMode ? (
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-sm font-medium text-ink"
                        htmlFor="directory-claim-agent"
                      >
                        Your branch
                      </label>
                      <select
                        className="h-[52px] w-full rounded-xl border border-line-strong bg-white px-4 text-base text-ink"
                        id="directory-claim-agent"
                        onChange={(event) => {
                          handleAgentChange(event.target.value);
                        }}
                        required
                        value={selectedAgentId}
                      >
                        <option disabled value="">
                          Choose your branch
                        </option>
                        {sortedAgentOptions.map((option) => {
                          const suffix =
                            option.postcode != null && option.postcode !== ''
                              ? ` · ${option.postcode}`
                              : '';

                          return (
                            <option key={option.id} value={option.id}>
                              {option.name}
                              {suffix}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ) : null}
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Work email</FormLabel>
                        <FormControl>
                          <input
                            autoComplete="email"
                            className="h-[52px] w-full rounded-xl border border-line-strong bg-white px-4 text-base text-ink"
                            placeholder="you@agency.co.uk"
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.formState.errors.root ? (
                    <p className="m-0 text-sm text-danger">
                      {form.formState.errors.root.message}
                    </p>
                  ) : null}
                  <button
                    className="button-primary h-12 disabled:pointer-events-none disabled:cursor-wait disabled:opacity-70"
                    disabled={
                      form.formState.isSubmitting || claimRequest.isRequesting
                    }
                    type="submit"
                  >
                    Request to claim profile
                  </button>
                </form>
              </Form>
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DirectoryAgentClaimRequestDialog;
